import { useEffect, useRef } from 'react'
import { router, usePoll } from '@inertiajs/react'

// @inertiajs/core is a transitive dependency (pulled in by @inertiajs/react), not a direct one —
// derive the requestOptions type from usePoll's own signature instead of importing from core.
type StatusPollRequestOptions = NonNullable<Parameters<typeof usePoll>[1]>

/**
 * Thin wrapper around Inertia's `usePoll` that adds a hard pause/resume on tab
 * visibility (E35-S35 AC8). `usePoll`'s own `keepAlive: false` default only
 * throttles background polling to 1-in-10 ticks rather than pausing it, and
 * never fires an immediate reload on refocus (see `@inertiajs/core`'s
 * `Poll#isInBackground`) — this wrapper stops the underlying poll entirely
 * while the tab is hidden, and on return reloads at once and restarts the poll,
 * so data that went stale while hidden is not shown for a full interval.
 *
 * @param enabled Gate beyond visibility, e.g. only poll while a given tab is active.
 */
export const useStatusPoll = (
  interval: number,
  requestOptions: StatusPollRequestOptions,
  enabled: boolean = true,
) => {
  const { start, stop } = usePoll(interval, requestOptions, { autoStart: false })
  const requestOptionsRef = useRef(requestOptions)
  requestOptionsRef.current = requestOptions

  useEffect(() => {
    if (!enabled) return

    if (document.visibilityState === 'visible') start()

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        router.reload(requestOptionsRef.current)
        start()
      } else {
        stop()
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange)
      stop()
    }
  }, [enabled, start, stop])

  return { start, stop }
}
